// Team Management Component for Firm Settings
import { useState, useEffect } from 'react';
import { useAuth } from '../lib/auth';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { toast } from 'sonner';
import {
  Users,
  UserPlus,
  Mail,
  MoreVertical,
  Edit,
  Trash2,
  Send,
  Shield,
  Loader2,
  Crown,
  CheckCircle,
  Clock,
  XCircle,
  AlertCircle,
  DollarSign,
  Settings
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Role display configuration
const roleConfig = {
  owner: { label: 'Owner', color: 'bg-amber-100 text-amber-700', icon: Crown },
  manager: { label: 'Manager', color: 'bg-purple-100 text-purple-700', icon: Shield },
  attorney: { label: 'Attorney', color: 'bg-blue-100 text-blue-700', icon: Users },
  paralegal: { label: 'Paralegal', color: 'bg-emerald-100 text-emerald-700', icon: Users },
  assistant: { label: 'Assistant', color: 'bg-slate-100 text-slate-700', icon: Users },
  other: { label: 'Other', color: 'bg-gray-100 text-gray-700', icon: Users }
};

const statusConfig = {
  active: { label: 'Active', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
  pending: { label: 'Pending', color: 'bg-amber-100 text-amber-700', icon: Clock },
  inactive: { label: 'Inactive', color: 'bg-slate-100 text-slate-700', icon: XCircle },
  suspended: { label: 'Suspended', color: 'bg-red-100 text-red-700', icon: AlertCircle }
};

// Permission labels
const permissionLabels = {
  can_manage_team: 'Manage Team Members',
  can_manage_firm_settings: 'Manage Firm Settings',
  can_view_all_cases: 'View All Cases',
  can_create_cases: 'Create Cases',
  can_edit_cases: 'Edit Cases',
  can_delete_cases: 'Delete Cases',
  can_view_billing: 'View Billing',
  can_manage_billing: 'Manage Billing',
  can_view_reports: 'View Reports',
  can_manage_documents: 'Manage Documents',
  can_log_time: 'Log Time',
  can_log_communications: 'Log Communications'
};

export default function TeamManagement() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [members, setMembers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [rolePermissions, setRolePermissions] = useState({});
  
  // Dialogs
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showPermissionsDialog, setShowPermissionsDialog] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);
  
  // Form state
  const [inviteForm, setInviteForm] = useState({
    email: '',
    name: '',
    role: 'attorney',
    billing_rate: ''
  });
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchData();
  }, [token]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [membersRes, invitesRes, permsRes] = await Promise.all([
        fetch(`${API_URL}/api/team/members`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_URL}/api/team/invitations`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_URL}/api/team/role-permissions`, { headers: { 'Authorization': `Bearer ${token}` } })
      ]);
      
      if (membersRes.ok) setMembers(await membersRes.json());
      if (invitesRes.ok) setInvitations(await invitesRes.json());
      if (permsRes.ok) setRolePermissions(await permsRes.json());
    } catch (error) {
      console.error('Failed to fetch team data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInvite = async () => {
    if (!inviteForm.email || !inviteForm.name) {
      toast.error('Please fill in email and name');
      return;
    }
    
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/team/invite`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...inviteForm,
          billing_rate: inviteForm.billing_rate ? parseFloat(inviteForm.billing_rate) : null
        })
      });
      
      if (res.ok) {
        toast.success(`Invitation sent to ${inviteForm.email}`);
        setShowInviteDialog(false);
        setInviteForm({ email: '', name: '', role: 'attorney', billing_rate: '' });
        fetchData();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to send invitation');
      }
    } catch (error) {
      toast.error('Failed to send invitation');
    } finally {
      setSaving(false);
    }
  };

  const handleResendInvite = async (invitation) => {
    try {
      const res = await fetch(`${API_URL}/api/team/invitations/${invitation.id}/resend`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success(`Invitation resent to ${invitation.email}`);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to resend invitation');
      }
    } catch (error) {
      toast.error('Failed to resend invitation');
    }
  };

  const handleCancelInvite = async (invitation) => {
    if (!window.confirm('Cancel this invitation?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/team/invitations/${invitation.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success('Invitation cancelled');
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to cancel invitation');
    }
  };

  const handleUpdateMember = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/team/members/${selectedMember.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...editForm,
          billing_rate: editForm.billing_rate ? parseFloat(editForm.billing_rate) : null
        })
      });
      
      if (res.ok) {
        toast.success('Team member updated');
        setShowEditDialog(false);
        fetchData();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to update member');
      }
    } catch (error) {
      toast.error('Failed to update member');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveMember = async (member) => {
    if (!window.confirm(`Remove ${member.name} from the team? They will lose access to all firm data.`)) return;
    
    try {
      const res = await fetch(`${API_URL}/api/team/members/${member.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success('Team member removed');
        fetchData();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to remove member');
      }
    } catch (error) {
      toast.error('Failed to remove member');
    }
  };

  const handleUpdateRolePermissions = async (role, permissions) => {
    try {
      const res = await fetch(`${API_URL}/api/team/role-permissions`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ role, permissions })
      });
      
      if (res.ok) {
        toast.success(`Permissions updated for ${roleConfig[role]?.label || role}`);
        fetchData();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to update permissions');
      }
    } catch (error) {
      toast.error('Failed to update permissions');
    }
  };

  const handleResetRolePermissions = async (role) => {
    if (!window.confirm(`Reset ${roleConfig[role]?.label || role} permissions to defaults?`)) return;
    
    try {
      const res = await fetch(`${API_URL}/api/team/role-permissions/${role}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success('Permissions reset to defaults');
        fetchData();
      }
    } catch (error) {
      toast.error('Failed to reset permissions');
    }
  };

  const openEditDialog = (member) => {
    setSelectedMember(member);
    setEditForm({
      name: member.name,
      role: member.role,
      billing_rate: member.billing_rate || '',
      status: member.status
    });
    setShowEditDialog(true);
  };

  const openPermissionsDialog = (role) => {
    setSelectedRole(role);
    setShowPermissionsDialog(true);
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-12 text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary" />
          <p className="mt-4 text-muted-foreground">Loading team data...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Tabs defaultValue="members">
        <TabsList>
          <TabsTrigger value="members" data-testid="team-members-tab">
            <Users className="w-4 h-4 mr-2" />
            Team Members ({members.length})
          </TabsTrigger>
          <TabsTrigger value="invitations" data-testid="invitations-tab">
            <Mail className="w-4 h-4 mr-2" />
            Pending Invitations ({invitations.length})
          </TabsTrigger>
          <TabsTrigger value="permissions" data-testid="permissions-tab">
            <Shield className="w-4 h-4 mr-2" />
            Role Permissions
          </TabsTrigger>
        </TabsList>

        {/* Team Members Tab */}
        <TabsContent value="members" className="space-y-4">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-lg font-semibold">Team Members</h3>
              <p className="text-sm text-muted-foreground">Manage your firm's team members and their roles</p>
            </div>
            <Button onClick={() => setShowInviteDialog(true)} data-testid="invite-member-btn">
              <UserPlus className="w-4 h-4 mr-2" />
              Invite Member
            </Button>
          </div>

          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Billing Rate</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-10"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {members.map((member) => {
                  const RoleIcon = roleConfig[member.role]?.icon || Users;
                  const StatusIcon = statusConfig[member.status]?.icon || CheckCircle;
                  
                  return (
                    <TableRow key={member.id} data-testid={`member-row-${member.id}`}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {member.is_owner && <Crown className="w-4 h-4 text-amber-500" />}
                          <span className="font-medium">{member.name}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground">{member.email}</TableCell>
                      <TableCell>
                        <Badge className={roleConfig[member.role]?.color || 'bg-gray-100'}>
                          {roleConfig[member.role]?.label || member.role}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {member.billing_rate ? (
                          <span className="flex items-center gap-1">
                            <DollarSign className="w-3 h-3" />
                            {member.billing_rate}/hr
                          </span>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={statusConfig[member.status]?.color}>
                          <StatusIcon className="w-3 h-3 mr-1" />
                          {statusConfig[member.status]?.label || member.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {!member.is_owner && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm">
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => openEditDialog(member)}>
                                <Edit className="w-4 h-4 mr-2" />
                                Edit
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onClick={() => handleRemoveMember(member)}
                                className="text-red-600"
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Remove
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        {/* Pending Invitations Tab */}
        <TabsContent value="invitations" className="space-y-4">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-lg font-semibold">Pending Invitations</h3>
              <p className="text-sm text-muted-foreground">Invitations that haven't been accepted yet</p>
            </div>
            <Button onClick={() => setShowInviteDialog(true)}>
              <UserPlus className="w-4 h-4 mr-2" />
              Invite Member
            </Button>
          </div>

          {invitations.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center">
                <Mail className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold mb-2">No Pending Invitations</h3>
                <p className="text-muted-foreground mb-4">All invitations have been accepted or expired</p>
                <Button onClick={() => setShowInviteDialog(true)}>
                  <UserPlus className="w-4 h-4 mr-2" />
                  Invite Team Member
                </Button>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Invited</TableHead>
                    <TableHead>Expires</TableHead>
                    <TableHead className="w-10"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invitations.map((invite) => (
                    <TableRow key={invite.id}>
                      <TableCell className="font-medium">{invite.name}</TableCell>
                      <TableCell className="text-muted-foreground">{invite.email}</TableCell>
                      <TableCell>
                        <Badge className={roleConfig[invite.role]?.color || 'bg-gray-100'}>
                          {roleConfig[invite.role]?.label || invite.role}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {new Date(invite.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {new Date(invite.expires_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleResendInvite(invite)}>
                              <Send className="w-4 h-4 mr-2" />
                              Resend
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => handleCancelInvite(invite)}
                              className="text-red-600"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Cancel
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          )}
        </TabsContent>

        {/* Role Permissions Tab */}
        <TabsContent value="permissions" className="space-y-4">
          <div>
            <h3 className="text-lg font-semibold">Role Permissions</h3>
            <p className="text-sm text-muted-foreground">Customize what each role can do in your firm</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.entries(rolePermissions)
              .filter(([role]) => role !== 'owner')
              .map(([role, data]) => {
                const RoleIcon = roleConfig[role]?.icon || Users;
                return (
                  <Card key={role} className={data.is_custom ? 'border-primary' : ''}>
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <RoleIcon className="w-5 h-5" />
                          <CardTitle className="text-base">{roleConfig[role]?.label || role}</CardTitle>
                        </div>
                        {data.is_custom && (
                          <Badge variant="secondary" className="text-xs">Custom</Badge>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="space-y-2 text-sm">
                        {Object.entries(data.permissions || {}).slice(0, 4).map(([perm, value]) => (
                          <div key={perm} className="flex items-center justify-between">
                            <span className="text-muted-foreground">{permissionLabels[perm] || perm}</span>
                            {value ? (
                              <CheckCircle className="w-4 h-4 text-emerald-500" />
                            ) : (
                              <XCircle className="w-4 h-4 text-muted-foreground" />
                            )}
                          </div>
                        ))}
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full"
                        onClick={() => openPermissionsDialog(role)}
                      >
                        <Settings className="w-4 h-4 mr-2" />
                        Edit Permissions
                      </Button>
                    </CardContent>
                  </Card>
                );
              })}
          </div>
        </TabsContent>
      </Tabs>

      {/* Invite Dialog */}
      <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Invite Team Member</DialogTitle>
            <DialogDescription>
              Send an invitation email to add a new team member to your firm.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="invite-name">Full Name *</Label>
              <Input
                id="invite-name"
                value={inviteForm.name}
                onChange={(e) => setInviteForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="John Smith"
                data-testid="invite-name-input"
              />
            </div>
            
            <div>
              <Label htmlFor="invite-email">Email Address *</Label>
              <Input
                id="invite-email"
                type="email"
                value={inviteForm.email}
                onChange={(e) => setInviteForm(prev => ({ ...prev, email: e.target.value }))}
                placeholder="john@example.com"
                data-testid="invite-email-input"
              />
            </div>
            
            <div>
              <Label htmlFor="invite-role">Role</Label>
              <Select
                value={inviteForm.role}
                onValueChange={(v) => setInviteForm(prev => ({ ...prev, role: v }))}
              >
                <SelectTrigger data-testid="invite-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="attorney">Attorney</SelectItem>
                  <SelectItem value="paralegal">Paralegal</SelectItem>
                  <SelectItem value="assistant">Assistant</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label htmlFor="invite-rate">Billing Rate ($/hr)</Label>
              <Input
                id="invite-rate"
                type="number"
                value={inviteForm.billing_rate}
                onChange={(e) => setInviteForm(prev => ({ ...prev, billing_rate: e.target.value }))}
                placeholder="250"
                data-testid="invite-rate-input"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInviteDialog(false)}>Cancel</Button>
            <Button onClick={handleInvite} disabled={saving} data-testid="send-invite-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
              Send Invitation
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Member Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Team Member</DialogTitle>
            <DialogDescription>
              Update {selectedMember?.name}'s details and role.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Name</Label>
              <Input
                value={editForm.name || ''}
                onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
              />
            </div>
            
            <div>
              <Label>Role</Label>
              <Select
                value={editForm.role || ''}
                onValueChange={(v) => setEditForm(prev => ({ ...prev, role: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="attorney">Attorney</SelectItem>
                  <SelectItem value="paralegal">Paralegal</SelectItem>
                  <SelectItem value="assistant">Assistant</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Billing Rate ($/hr)</Label>
              <Input
                type="number"
                value={editForm.billing_rate || ''}
                onChange={(e) => setEditForm(prev => ({ ...prev, billing_rate: e.target.value }))}
                placeholder="250"
              />
            </div>
            
            <div>
              <Label>Status</Label>
              <Select
                value={editForm.status || ''}
                onValueChange={(v) => setEditForm(prev => ({ ...prev, status: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                  <SelectItem value="suspended">Suspended</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
            <Button onClick={handleUpdateMember} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Permissions Dialog */}
      <Dialog open={showPermissionsDialog} onOpenChange={setShowPermissionsDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              Edit {roleConfig[selectedRole]?.label || selectedRole} Permissions
            </DialogTitle>
            <DialogDescription>
              Customize what users with this role can do.
            </DialogDescription>
          </DialogHeader>
          
          {selectedRole && rolePermissions[selectedRole] && (
            <div className="space-y-4 max-h-[400px] overflow-y-auto">
              {Object.entries(permissionLabels).map(([perm, label]) => (
                <div key={perm} className="flex items-center justify-between">
                  <Label htmlFor={perm} className="cursor-pointer">{label}</Label>
                  <Switch
                    id={perm}
                    checked={rolePermissions[selectedRole]?.permissions?.[perm] || false}
                    onCheckedChange={(checked) => {
                      const newPerms = {
                        ...rolePermissions[selectedRole]?.permissions,
                        [perm]: checked
                      };
                      handleUpdateRolePermissions(selectedRole, newPerms);
                    }}
                  />
                </div>
              ))}
            </div>
          )}
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => handleResetRolePermissions(selectedRole)}
            >
              Reset to Defaults
            </Button>
            <Button onClick={() => setShowPermissionsDialog(false)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
